import { isNil } from 'lodash'

import {
  DEFAULT_FORBIDDEN_MESSAGE,
  hasOwnDenialReason,
  isPermissionDenied,
  PERMISSION_DENIED_MESSAGE,
  toast,
} from '@/components/toasts/toast.helpers'

import { unauthenticateUser } from '../reducers/auth/currentUser.actions'
import { history } from '../entry'

const getFormattedError = (response, message) => {
  const errObj = new Error(message)

  return Object.assign(errObj, { status: response.status, ...response })
}

/**
 * The message for a rejection from fetchingAPI when the backend authored it,
 * else `fallback`. A 403 that only names a capability, or has no reason of its
 * own, returns PERMISSION_DENIED_MESSAGE ("ask an admin") instead.
 *
 * Two things have to be true for a rejection's text to be worth showing.
 *
 * It has to have come from the API. handleResponse rejects either with the
 * parsed response body (400, 409, 444, 502 — a plain object, typically
 * `{ message }`) or with an Error tagged with `status`. A `throw new Error(...)`
 * inside an action has neither marker, and its text is developer-facing —
 * "returned no taxonomy_uuid" is not something to put in front of an admin.
 *
 * And it has to be a 4xx. A 4xx is the backend telling the caller what is wrong
 * with their request, which is exactly the text a fixed "Failed to save…" was
 * hiding. A 5xx is the system failing: handleResponse ignores the response body
 * there and substitutes its own boilerplate ("Internal server error. Please try
 * again later…"), which says less than the caller's own fallback — the caller at
 * least knows which operation died. So 5xx keeps the fallback.
 *
 * Use this instead of `err?.message` wherever a catch block would otherwise
 * replace a backend validation message with a fixed string.
 *
 * NOTE: `voiceAgents.actions.js` has a local helper of the same name and
 * signature but different semantics — it also reads `detail`, accepts a bare
 * string, and does not distinguish a local `throw` from an API rejection. The
 * two have not been unified; check which one you're looking at.
 *
 * @param {*} err The value a fetchingAPI call rejected with
 * @param {string} fallback Shown when the rejection carries no such message and
 *   isn't a 403
 * @returns {string}
 */
export const apiErrorMessage = (err, fallback) => {
  // A capability 403's body names the missing capability ("Missing permission:
  // users:write"), which means nothing to the user; say what they can do about it
  // instead. So does a 403 with no body of its own. A 403 whose backend wrote its
  // own reason (an org or ownership check, say) keeps that reason.
  if (isPermissionDenied(err) && !hasOwnDenialReason(err)) {
    return PERMISSION_DENIED_MESSAGE
  }

  // A parsed body is only ever forwarded for statuses handleResponse does not
  // rewrite, so reaching here as a non-Error already means the backend's own
  // words; the status check applies to the Error-shaped half.
  const backendAuthored = !(err instanceof Error) || (!isNil(err.status) && err.status < 500)

  return (backendAuthored && err?.message) || fallback
}

/**
 * handleResponse takes the result of a fetch and handles it accordingly.
 *
 * @param {Object} response The result of a fetch
 * @param {Function} dispatch Redux dispatch function
 * @param {Function} callback A callback function that can
 * @param {*} details An optional details object for modifying this handler
 * @returns {Promise} A resolved Promise
 */
export const handleResponse = (response, dispatch, callback, details = {}) => {
  return new Promise((resolve, reject) => {
    let formattedError = null

    switch (response.status) {
      case 200: // OK
        if (callback) {
          callback()
        }

        if (
          ['text/csv', 'text/csv; charset=utf-8'].includes(response.headers.get('Content-type'))
        ) {
          return resolve(response.body)
        }

        return resolve(response.json())
      case 201: // Created
        return resolve(response.json())
      case 202: // Accepted
        return resolve(response.json())
      case 204: // No Content
        return resolve()
      case 400: // Bad Request
        return response.json().then((res) => reject(res))
      case 401: // Unauthorized
        return reject(dispatch(unauthenticateUser({ history })))
      case 403: // Forbidden
        return response
          .json()
          .catch(() => ({}))
          .then((res) => {
            // core-service puts its 403 reason in `error`. A capability name there
            // stays out of err.message, because callers that render err.message
            // directly would show it to users; the toast and apiErrorMessage turn
            // the placeholder into the "ask an admin" text instead.
            const ownError = hasOwnDenialReason({ message: res?.error }) ? res.error : undefined
            formattedError = getFormattedError(
              response,
              res?.message || res?.detail || ownError || DEFAULT_FORBIDDEN_MESSAGE
            )
            formattedError.isPermissionDenied = true

            return reject(formattedError)
          })
      case 404: // Not Found
        return response.json().then((res) => {
          formattedError = getFormattedError(response, res?.message || 'Not found.')
          // Carry the body's `kind` discriminator through, the way the 429 case
          // carries `data`. Some endpoints answer 404 for more than one reason
          // (e.g. voice-agents' /prompts/config/by-user: dead link vs. an agent
          // you own that just isn't externalized yet) and only `kind` separates
          // them — string-matching `detail` is not a contract.
          formattedError.kind = res?.kind
          formattedError.data = res

          return reject(formattedError)
        })
      case 409: // Conflict
        return response.json().then((res) => reject(res))
      case 422: // Unprocessable Entity
        return response
          .json()
          .catch(() => ({}))
          .then((res) => {
            // FastAPI's auto-generated 422 puts a Pydantic error list in
            // `detail` (e.g. [{loc, msg, type}, ...]) which renders as
            // [object Object]. Fall back to the generic message in that case.
            const friendly =
              res?.message ||
              (typeof res?.detail === 'string' ? res.detail : null) ||
              'Request could not be processed.'
            formattedError = getFormattedError(response, friendly)
            // Carry the list through as well, the way the 404 branch carries
            // `kind`. `getFormattedError` builds from the fetch Response, which
            // holds none of the body — without this a caller that can render a
            // field error (which one, and why) only ever sees the canned text.
            formattedError.detail = res?.detail
            formattedError.kind = res?.kind
            return reject(formattedError)
          })
      case 429: // Too Many Requests
        return response.json().then((res) => {
          formattedError = getFormattedError(
            response,
            res?.detail?.message || 'Rate limit exceeded.'
          )
          formattedError.data = res
          return reject(formattedError)
        })
      case 423: // Locked
        // TODO: This is only used in createPlaybookVersion when two users edit the same playbook.
        // The /api/configs POST returns 423 as a string. We should make it return JSON instead.
        //
        // Here is the relevant Python code:
        //   except exceptions.JSONMergeError as error:
        //     return (str(error), 423)
        formattedError = getFormattedError(
          response,
          'Another user saved a new version while you were editing. Please note your changes, navigate to the newest version, and try again.'
        )
        return reject(formattedError)
      case 444: // Call ongoing
        return response.json().then((res) => reject(res))
      case 500: // Internal Server Error
        formattedError = getFormattedError(
          response,
          'Internal server error. Please try again later. If the problem persists, please reach out to support@baltosoftware.com.'
        )
        if (!details.isSilent) {
          toast.error(formattedError.message)
        }
        return reject(formattedError)

      case 502: // Gateway Timeout
        return response.json().then((res) => reject(res))
      case 504: // Timeout
        formattedError = getFormattedError(
          response,
          'Connection timed out. Try this query over a shorter time range. If the problem persists, reach out to support@baltosoftware.com.'
        )

        return reject(formattedError)
      default:
        formattedError = getFormattedError(
          response,
          'An unknown error occurred. If the problem persists, please reach out to support@baltosoftware.com.'
        )
        if (!details.isSilent) {
          toast.error(formattedError.message)
        }
        return reject(formattedError)
    }
  })
}
